/**
 * UserTasksView Component
 *
 * Full Kanban board for user task management:
 * - Kanban board (Backlog, Todo, Doing, Agent Test, User Test, Review, Done)
 * - Drag-and-drop task movement
 * - Task popup/detail panel
 * - Project selector (filters to user's owned projects)
 * - Create new tasks
 *
 * @module components/user/UserTasksView
 */

import { memo, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Loader2, AlertCircle, RefreshCw, AlertTriangle, ListTodo } from 'lucide-react';
import TaskBoard from '../tasks/TaskBoard.jsx';
import TaskDetail from '../tasks/TaskDetail.jsx';
import TaskForm from '../tasks/TaskForm.jsx';
import ConfirmDialog from '../common/ConfirmDialog.jsx';
import {
  useTasks,
  useTask,
  useTaskComments,
  useTaskHistory,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useUpdateTaskStatus,
  useLabels,
  useProjectAgents
} from '../../hooks/useTasks.js';
import { useTaskSubscription } from '../../hooks/useTaskSubscription.js';
import { useWebSocketState, CONNECTION_STATE } from '../../hooks/useWebSocket.js';

// ============================================
// Constants
// ============================================

const TASK_STATUS_ORDER = ['backlog', 'todo', 'doing', 'agent_test', 'user_test', 'review', 'done', 'blocked'];

// ============================================
// API Functions
// ============================================

async function fetchProjects() {
  const response = await fetch('/api/projects', {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch projects: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

async function fetchLabels() {
  const response = await fetch('/api/labels', {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch labels: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

// ============================================
// Main Component
// ============================================

const UserTasksView = memo(function UserTasksView() {
  // ========================================
  // State
  // ========================================

  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [highlightedTaskIds, setHighlightedTaskIds] = useState(new Set());
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, task: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Project state
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [projectsError, setProjectsError] = useState(null);

  // Labels state
  const [labels, setLabels] = useState([]);

  const highlightTimeoutsRef = useRef(new Map());

  // ========================================
  // Load Projects on Mount
  // ========================================

  const loadProjects = useCallback(async () => {
    setIsLoadingProjects(true);
    setProjectsError(null);
    try {
      const [projectsData, labelsData] = await Promise.all([
        fetchProjects(),
        fetchLabels()
      ]);
      setProjects(projectsData);
      setLabels(labelsData);
      // Auto-select first project if none selected
      if (projectsData.length > 0 && !selectedProject) {
        setSelectedProject(projectsData[0]);
      }
    } catch (err) {
      setProjectsError(err.message);
    } finally {
      setIsLoadingProjects(false);
    }
  }, [selectedProject]);

  // Load on mount
  useEffect(() => {
    loadProjects();
  }, []);

  // ========================================
  // Highlight Effect for Live Updates
  // ========================================

  const highlightTask = useCallback((taskId, duration = 3000) => {
    setHighlightedTaskIds(prev => new Set([...prev, taskId]));

    const existingTimeout = highlightTimeoutsRef.current.get(taskId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const timeoutId = setTimeout(() => {
      setHighlightedTaskIds(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
      highlightTimeoutsRef.current.delete(taskId);
    }, duration);

    highlightTimeoutsRef.current.set(taskId, timeoutId);
  }, []);

  // ========================================
  // Data Fetching with React Query
  // ========================================

  const {
    data: tasksData,
    isLoading: isLoadingTasks,
    error: tasksError,
    refetch: refetchTasks
  } = useTasks(
    { projectId: selectedProject?.id, limit: 100 },
    { enabled: !!selectedProject?.id }
  );

  const {
    data: selectedTask,
    isLoading: isLoadingTask
  } = useTask(selectedTaskId, { enabled: !!selectedTaskId });

  const {
    data: commentsData,
    isLoading: isLoadingComments
  } = useTaskComments(selectedTaskId, {}, { enabled: !!selectedTaskId });

  const {
    data: historyData,
    isLoading: isLoadingHistory
  } = useTaskHistory(selectedTaskId, {}, { enabled: !!selectedTaskId });

  // Fetch assignees for the project
  const { data: assigneesData = [] } = useProjectAgents(selectedProject?.id, { enabled: !!selectedProject?.id });

  // ========================================
  // Mutations
  // ========================================

  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const updateTaskStatus = useUpdateTaskStatus();

  // ========================================
  // WebSocket State & Subscription
  // ========================================

  const { state: wsState, isConnected: wsConnected } = useWebSocketState();

  const { isSubscribed } = useTaskSubscription(selectedProject?.id, {
    enabled: !!selectedProject?.id,
    onTaskCreated: (task) => {
      highlightTask(task.id);
    },
    onTaskUpdated: (taskId) => {
      highlightTask(taskId);
    },
    onTaskStatusChanged: (taskId) => {
      highlightTask(taskId);
    },
    onTaskAssigned: (taskId) => {
      highlightTask(taskId);
    },
    onTaskUnassigned: (taskId) => {
      highlightTask(taskId);
    },
    onTaskCommentAdded: (taskId) => {
      highlightTask(taskId);
    }
  });

  // ========================================
  // Computed Values
  // ========================================

  // Group tasks by status for the Kanban board
  const tasksByStatus = useMemo(() => {
    const tasks = tasksData?.data || tasksData?.items || [];
    if (tasks.length === 0) return {};

    const grouped = {};
    TASK_STATUS_ORDER.forEach(status => {
      grouped[status] = tasks.filter(t => t.status === status);
    });

    return grouped;
  }, [tasksData]);

  // Check if tasks are truncated
  const tasksTruncated = useMemo(() => {
    const pagination = tasksData?.pagination;
    if (!pagination) return null;
    const loadedCount = tasksData?.data?.length || tasksData?.items?.length || 0;
    if (pagination.total > loadedCount) {
      return {
        loaded: loadedCount,
        total: pagination.total,
        hidden: pagination.total - loadedCount
      };
    }
    return null;
  }, [tasksData]);

  // WebSocket status object
  const wsStatusObj = useMemo(() => ({
    isConnected: wsConnected,
    isSubscribed,
    state: wsState === CONNECTION_STATE.RECONNECTING ? 'reconnecting' : wsConnected ? 'connected' : 'disconnected'
  }), [wsConnected, isSubscribed, wsState]);

  // ========================================
  // Handlers
  // ========================================

  const handleTaskClick = useCallback((task) => {
    setSelectedTaskId(task.id);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedTaskId(null);
  }, []);

  const handleCreateTask = useCallback(() => {
    setEditingTask(null);
    setIsFormOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingTask(null);
  }, []);

  const handleSubmitForm = useCallback(async (data) => {
    try {
      if (editingTask) {
        await updateTask.mutateAsync({ id: editingTask.id, data });
      } else {
        await createTask.mutateAsync({ ...data, projectId: selectedProject.id });
      }
      handleCloseForm();
      refetchTasks();
    } catch (error) {
      console.error('Failed to save task:', error);
    }
  }, [editingTask, updateTask, createTask, selectedProject?.id, handleCloseForm, refetchTasks]);

  const handleDeleteTask = useCallback((task) => {
    setDeleteDialog({ isOpen: true, task });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteDialog.task) return;
    setIsDeleting(true);
    try {
      await deleteTask.mutateAsync(deleteDialog.task.id);
      if (selectedTaskId === deleteDialog.task.id) {
        setSelectedTaskId(null);
      }
      setDeleteDialog({ isOpen: false, task: null });
    } catch (error) {
      console.error('Failed to delete task:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteDialog.task, deleteTask, selectedTaskId]);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialog({ isOpen: false, task: null });
  }, []);

  const handleStatusChange = useCallback(async (taskId, newStatus) => {
    try {
      await updateTaskStatus.mutateAsync({ id: taskId, status: newStatus });
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  }, [updateTaskStatus]);

  const handleSaveTask = useCallback(async (taskId, data) => {
    try {
      await updateTask.mutateAsync({ id: taskId, data });
      refetchTasks();
      return true;
    } catch (error) {
      console.error('Failed to save task:', error);
      return false;
    }
  }, [updateTask, refetchTasks]);

  const handleProjectChange = useCallback((project) => {
    setSelectedProject(project);
    setSelectedTaskId(null);
  }, []);

  // ========================================
  // Render
  // ========================================

  // Loading projects state
  if (isLoadingProjects) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading projects...
      </div>
    );
  }

  // Projects error state
  if (projectsError) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-red-400">
        <AlertCircle className="w-8 h-8 mb-2" />
        <p className="mb-4">Failed to load projects</p>
        <button
          onClick={loadProjects}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      </div>
    );
  }

  // No projects available
  if (projects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <div className="p-3 rounded-full bg-gray-800 mb-4">
          <ListTodo className="w-8 h-8 text-gray-500" />
        </div>
        <h3 className="text-lg font-medium text-gray-300 mb-1">No Projects Available</h3>
        <p className="text-sm text-gray-500">
          Create a project first to manage tasks.
        </p>
      </div>
    );
  }

  // No project selected (shouldn't happen with auto-select)
  if (!selectedProject) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <AlertCircle className="w-6 h-6 mr-2" />
        Select a project to view tasks
      </div>
    );
  }

  // Loading tasks
  if (isLoadingTasks) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading tasks...
      </div>
    );
  }

  // Tasks error
  if (tasksError) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-red-400">
        <AlertCircle className="w-8 h-8 mb-2" />
        <p className="mb-4">Failed to load tasks</p>
        <button
          onClick={() => refetchTasks()}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Truncation Warning Banner */}
      {tasksTruncated && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 border-b border-amber-500/30 text-amber-300 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            Showing {tasksTruncated.loaded} of {tasksTruncated.total} tasks.
            {tasksTruncated.hidden} task{tasksTruncated.hidden !== 1 ? 's' : ''} not displayed due to limit.
          </span>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main Board Area */}
        <div className="flex-1 overflow-hidden">
          <TaskBoard
            tasksByStatus={tasksByStatus}
            onTaskClick={handleTaskClick}
            onStatusChange={handleStatusChange}
            onCreateTask={handleCreateTask}
            highlightedTaskIds={highlightedTaskIds}
            isPopout={false}
            showPopoutButton={true}
            wsStatus={wsStatusObj}
            projects={projects}
            selectedProject={selectedProject}
            onProjectChange={handleProjectChange}
          />
        </div>

        {/* Detail Panel */}
        {selectedTaskId && (
          <div className="w-96 border-l border-gray-800 overflow-y-auto">
            <TaskDetail
              task={selectedTask?.data || selectedTask}
              comments={commentsData?.data || commentsData?.items || []}
              history={historyData?.data || historyData?.items || []}
              assignees={assigneesData}
              isLoadingComments={isLoadingComments}
              isLoadingHistory={isLoadingHistory}
              onClose={handleCloseDetail}
              onSave={handleSaveTask}
              onDelete={handleDeleteTask}
              isSaving={updateTask.isPending}
            />
          </div>
        )}
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg mx-4">
            <TaskForm
              task={editingTask}
              projects={[selectedProject]}
              labels={labels}
              assignees={assigneesData}
              onSubmit={handleSubmitForm}
              onCancel={handleCloseForm}
              isSubmitting={createTask.isPending || updateTask.isPending}
              submitError={createTask.error?.message || updateTask.error?.message}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title="Delete Task"
        itemName={deleteDialog.task?.title}
        warning="This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isProcessing={isDeleting}
      />
    </div>
  );
});

export default UserTasksView;
export { UserTasksView };
